import {GenOutageAggregationLevel} from 'generated/serverModels/GenOutageAggregationLevel';
import {ApplicationConfig} from 'src/app/shared/classes/application-config';
import {DataService} from 'src/app/shared/services/data.service';
import {
    BaseMapConfig,
    BaseMapQueryParameter,
    GeoBounds,
    MapInitOptions,
} from 'src/app/routes/map/vulcan-map-adapter/abstractions/map-abstraction.types';
import {VulcanMapAdapterDefaultOptions} from 'src/app/routes/map/vulcan-map-adapter/config/vulcan-map-adapter.config';
import {AdapterBaseLayerInfo} from 'src/app/routes/map/vulcan-map-adapter/shared/map-base-layer-presets';

/** Browser configuration values supplied by EAGLE-I's generated environment script. */
interface EagleIMapWindow extends Window {
    /** Values that the server makes available before Angular starts. */
    eaglei?: {
        /** ArcGIS token used only for Esri basemap requests. */
        baselayerKey?: string;
    };
}

const FALLBACK_CONUS_BOUNDS: GeoBounds = {
    southWest: {lat: 24.396308, lng: -124.848974},
    northEast: {lat: 49.384358, lng: -66.885444},
};

/** Esri's supported United States edition, which uses the Gulf of America label. */
type EagleIArcgisWorldview = 'unitedStatesOfAmerica';

const UNITED_STATES_ARCGIS_WORLDVIEW: EagleIArcgisWorldview = 'unitedStatesOfAmerica';
export const DEFAULT_EAGLE_I_BASE_LAYER_ID = 'arcgis/topographic';
const ARCGIS_BASEMAP_STYLES_URL = 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles';
const ARCGIS_ATTRIBUTION =
    'Powered by <a target="_blank" href="https://www.esri.com/en-us/home">Esri</a> | ' +
    'Esri, TomTom, Garmin, FAO, NOAA, USGS, OpenStreetMap contributors, and the GIS User Community';

/** Builds the EAGLE-I basemap choices with the runtime ArcGIS token and United States worldview. */
export function getEagleIAdapterBaseLayers(): AdapterBaseLayerInfo[] {
    const accessToken = (window as EagleIMapWindow).eaglei?.baselayerKey;

    return [
        createEagleIArcgisBaseLayer('Topographic', 'arcgis/topographic', 'assets/images/base-layers/topo.png', {
            accessToken,
            worldview: UNITED_STATES_ARCGIS_WORLDVIEW,
        }),
        createEagleIArcgisBaseLayer('Dark Gray', 'arcgis/dark-gray', 'assets/images/base-layers/dark-gray.png', {
            accessToken,
            worldview: UNITED_STATES_ARCGIS_WORLDVIEW,
        }),
        createEagleIArcgisBaseLayer('Imagery', 'arcgis/imagery', 'assets/images/base-layers/imagery.png', {
            accessToken,
            worldview: UNITED_STATES_ARCGIS_WORLDVIEW,
        }),
        createEagleIArcgisBaseLayer('Streets', 'arcgis/streets', 'assets/images/base-layers/street.png', {
            accessToken,
            worldview: UNITED_STATES_ARCGIS_WORLDVIEW,
        }),
    ];
}

/** Shared EAGLE-I map settings that each map owner may use or extend. */
export interface EagleIMapDefaultOptions extends VulcanMapAdapterDefaultOptions {
    /** Default camera, zoom bounds, behavior, and controls. */
    mapInitOptions: MapInitOptions;
    /** Basemap choices shown by application map controls. */
    baseLayers: readonly AdapterBaseLayerInfo[];
    /** Basemap applied when a map owner does not choose another one. */
    defaultBaseLayerId: string;
}

/** Builds EAGLE-I's shared map defaults without choosing or creating a concrete adapter. */
export function getEagleIVulcanMapAdapterDefaultOptions(): EagleIMapDefaultOptions {
    return {
        mapInitOptions: {
            center: {lat: 38, lng: -96},
            zoom: 4,
            resetBounds: getEagleIConusBounds(),
            minZoom: 2,
            maxZoom: 20,
            behavior: {
                allowPanning: true,
                showPanningControl: false,
            },
        },
        baseLayers: getEagleIAdapterBaseLayers(),
        defaultBaseLayerId: DEFAULT_EAGLE_I_BASE_LAYER_ID,
    };
}

/** Returns the CONUS state extent shared by location fitting and every Vulcan reset control. */
export function getEagleIConusBounds(): GeoBounds {
    const nonConusStates = new Set(DataService.nonConusStateAbbreviations);
    const stateGeometries = ApplicationConfig.geometries.get(GenOutageAggregationLevel.state) ?? [];
    const bounds = {minLat: Infinity, minLng: Infinity, maxLat: -Infinity, maxLng: -Infinity};

    stateGeometries
        .filter((state) => !nonConusStates.has(state.name))
        .forEach((state) => {
            try {
                extendBounds(JSON.parse(state.boundary) as unknown, bounds);
            } catch {
                // One invalid state geometry must not prevent reset from using the remaining CONUS states.
            }
        });

    if (![bounds.minLat, bounds.minLng, bounds.maxLat, bounds.maxLng].every(Number.isFinite)) {
        return FALLBACK_CONUS_BOUNDS;
    }

    return {
        southWest: {lat: bounds.minLat, lng: bounds.minLng},
        northEast: {lat: bounds.maxLat, lng: bounds.maxLng},
    };
}

/** Builds one worldview-aware Esri vector basemap style owned by EAGLE-I application configuration. */
function createEagleIArcgisBaseLayer(
    displayName: string,
    id: string,
    icon: string,
    options: {accessToken?: string; worldview: EagleIArcgisWorldview},
): AdapterBaseLayerInfo {
    const queryParameters: Record<string, BaseMapQueryParameter> = {
        worldview: options.worldview,
    };

    if (options.accessToken) {
        queryParameters['token'] = options.accessToken;
    }

    const config: BaseMapConfig = {
        id,
        styleUrl: `${ARCGIS_BASEMAP_STYLES_URL}/${id}`,
        options: {
            attribution: ARCGIS_ATTRIBUTION,
            opacity: 1,
            queryParameters,
        },
    };

    return {displayName, id, icon, config};
}

/** Extends one mutable bounds accumulator from Polygon and MultiPolygon coordinate nesting. */
function extendBounds(value: unknown, bounds: {minLat: number; minLng: number; maxLat: number; maxLng: number}): void {
    if (!Array.isArray(value)) {
        if (value && typeof value === 'object' && 'coordinates' in value) {
            extendBounds((value as {coordinates: unknown}).coordinates, bounds);
        }
        return;
    }

    if (value.length >= 2 && value.every((coordinate) => typeof coordinate === 'number')) {
        const [lng, lat] = value as number[];
        bounds.minLat = Math.min(bounds.minLat, lat);
        bounds.minLng = Math.min(bounds.minLng, lng);
        bounds.maxLat = Math.max(bounds.maxLat, lat);
        bounds.maxLng = Math.max(bounds.maxLng, lng);
        return;
    }

    value.forEach((coordinate) => extendBounds(coordinate, bounds));
}
